/* Theme-owned entrance animation. Source contract: animation-contract.json.
 * Reinitialization is allowed; there is deliberately no permanent played flag.
 */
(function installNativeEntryAnimation() {
  'use strict';

  // Several native sections may include this asset. Register lifecycle hooks once.
  if (window.NativeEntryAnimation) return;

  const selector = '[data-native-entry-animation]';
  const visibleRatio = 0.25;
  const animatedClass = 'np-brand-animated';
  const currentState = new WeakMap();

  function cancelPreviousState(element) {
    const previous = currentState.get(element);
    if (!previous) return;
    if (previous.observer) previous.observer.unobserve(element);
    previous.cleanupTimers.forEach(timer => window.clearTimeout(timer));
    previous.cleanupTimers.clear();
  }

  function play(element, animation, state) {
    if (currentState.get(element) !== state) return;
    element.style.visibility = 'visible';
    element.classList.add(animatedClass, animation);
    element.onanimationend = function onAnimationEnd() {
      if (currentState.get(element) !== state) return;
      const timer = window.setTimeout(function removeAnimatedClass() {
        state.cleanupTimers.delete(timer);
        if (currentState.get(element) === state) {
          element.classList.remove(animatedClass);
        }
      }, 2000);
      state.cleanupTimers.add(timer);
    };
  }

  function isVisibleEnough(entry) {
    if (!entry.isIntersecting) return false;
    const rootHeight = entry.rootBounds
      ? entry.rootBounds.height
      : window.innerHeight;
    return entry.intersectionRatio >= visibleRatio ||
      entry.boundingClientRect.height * visibleRatio > rootHeight;
  }

  function init(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const elements = Array.from(root.querySelectorAll(selector));
    if (typeof root.matches === 'function' && root.matches(selector)) {
      elements.unshift(root);
    }
    const observers = new Map();

    elements.forEach(function initializeElement(element) {
      const animation = element.getAttribute('data-native-entry-animation');
      if (!animation) return;
      cancelPreviousState(element);

      const state = { observer: null, cleanupTimers: new Set() };
      currentState.set(element, state);
      if (animation.toLowerCase().includes('in')) {
        element.style.visibility = 'hidden';
      }

      if (!window.IntersectionObserver) {
        play(element, animation, state);
        return;
      }

      const offset = Number(element.getAttribute('data-native-offset')) || 0;
      let observer = observers.get(offset);
      if (!observer) {
        observer = new window.IntersectionObserver(function handleIntersections(entries, activeObserver) {
          entries.forEach(function handleEntry(entry) {
            const activeState = currentState.get(entry.target);
            if (!activeState || activeState.observer !== activeObserver) return;
            if (!isVisibleEnough(entry)) return;
            play(entry.target, entry.target.getAttribute('data-native-entry-animation'), activeState);
            activeObserver.unobserve(entry.target);
            activeState.observer = null;
          });
        }, {
          threshold: [0, visibleRatio],
          rootMargin: `0px 0px ${-offset}px 0px`
        });
        observers.set(offset, observer);
      }
      state.observer = observer;
      observer.observe(element);
    });
  }

  window.NativeEntryAnimation = { init };
  document.addEventListener('shopify:section:load', function onSectionLoad(event) {
    init(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function onReady() {
      init(document);
    }, { once: true });
  } else {
    init(document);
  }
})();

/* Theme-owned controls for the explicitly audited awards rail. Source ContentList GRID nodes do not initialize controls.
 * Source contracts: runtime-source/carousel-contract.json. No external runtime. */
(() => {
  const states = new WeakMap();
  const device = () => innerWidth <= 767 ? 'mobile' : innerWidth <= 1024 ? 'tablet' : innerWidth <= 1200 ? 'laptop' : 'all';
  function initialize(owner) {
    states.get(owner)?.destroy();
    const config = JSON.parse(owner.dataset.nativeCarousel);
    const track = owner.classList.contains('np-brand-slider') ? owner : owner.querySelector('.np-brand-slider');
    if (!track) throw new Error('Missing native carousel track');
    const slides = [...track.children].filter(node => node.classList.contains('np-brand-slide'));
    if (!slides.length) throw new Error('Missing native carousel slides');
    if (config.maxHeight !== true) throw new Error('Unaudited variable-height carousel');
    const abort = new AbortController(), { signal } = abort;
    const previous = track.querySelector(':scope > .np-brand-previous');
    const next = track.querySelector(':scope > .np-brand-next');
    const pagination = track.querySelector(':scope > .np-brand-pagination');
    let current = 0, viewport, timer, scrollTimer, visibleObserver, pendingTarget = null, pendingManual = false;
    const show = () => Number(config.slidesToShow[viewport]);
    const step = () => Number(config.slidesToScroll[viewport]);
    const finalIndex = () => Math.max(0, slides.length - show());
    const pageCount = () => Math.max(1, Math.ceil((slides.length - show()) / step()) + 1);
    function update() {
      if (pagination && config.paginationStyle !== 'none') {
        const selected = Math.min(Math.ceil(current / step()), pagination.children.length - 1);
        [...pagination.children].forEach((button, index) => {
          button.classList.toggle('np-brand-active', index === selected);
          button.setAttribute('aria-current', index === selected ? 'true' : 'false');
        });
      }
      if (previous && next && config.navStyle !== 'none' && !config.loop) {
        previous.style.visibility = current <= 0 || slides.length <= show() ? 'hidden' : '';
        next.style.visibility = current >= finalIndex() || slides.length <= show() ? 'hidden' : '';
      }
    }
    function releasePendingCommand() {
      pendingTarget = null;
      pendingManual = false;
      clearTimeout(scrollTimer);
    }
    function commandTargetReached() {
      return pendingTarget === null || Math.abs(track.scrollLeft - pendingTarget) <= 1;
    }
    function go(index, manual = false) {
      clearTimeout(scrollTimer);
      current = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[current];
      const physicalTarget = Math.min(Math.max(0, track.scrollWidth - track.clientWidth), Math.max(0, slide.offsetLeft));
      pendingTarget = Math.abs(track.scrollLeft - physicalTarget) <= 1 ? null : physicalTarget;
      pendingManual = manual && pendingTarget !== null;
      track.scroll({ left: Math.min(track.scrollWidth, Math.max(0, slide.offsetLeft)), top: Math.min(track.scrollHeight, Math.max(0, slide.offsetTop)), behavior: 'smooth' });
      update();
    }
    function move(direction, manual = false) {
      if (direction > 0) {
        if (current >= finalIndex()) { if (config.loop) go(0, manual); }
        else go(Math.min(finalIndex(), current + step()), manual);
      } else if (current === 0) { if (config.loop) go(finalIndex(), manual); }
      else go(Math.max(0, current - step()), manual);
    }
    function startTimer() {
      clearInterval(timer);
      if (config.autoPlay === true) {
        timer = setInterval(() => {
          if (!commandTargetReached()) return;
          if (pendingManual) {
            releasePendingCommand();
            startTimer();
            return;
          }
          pendingTarget = null;
          let target = current + step();
          if (config.loop && (current + 1 === slides.length || current + 1 + show() > slides.length)) target = 0;
          if (slides[target]) go(target);
        }, Number(config.autoPlayDelay));
      }
    }
    function measure() {
      const nextViewport = device();
      releasePendingCommand();
      if (viewport === nextViewport) return;
      viewport = nextViewport;
      slides.forEach((slide, index) => { slide.style.scrollSnapStop = index % step() === 0 ? 'always' : ''; });
      if (pagination && config.paginationStyle !== 'none') {
        pagination.replaceChildren();
        for (let index = 0; index < pageCount(); index++) {
          const button = document.createElement('button');
          button.type = 'button'; button.setAttribute('aria-label', `Go to slide ${index + 1}`);
          button.addEventListener('click', event => { event.stopPropagation(); go(index * step(), true); startTimer(); }, { signal });
          pagination.append(button);
        }
      }
      // maxHeight=true retains the source CSS's natural full-rail height.
      update(); startTimer();
    }
    next?.addEventListener('click', event => { event.stopPropagation(); if (!event.detail || event.detail === 1) { move(1, true); startTimer(); } }, { signal });
    previous?.addEventListener('click', event => { event.stopPropagation(); if (!event.detail || event.detail === 1) { move(-1, true); startTimer(); } }, { signal });
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (!commandTargetReached()) return;
        const wasManual = pendingManual;
        pendingTarget = null;
        pendingManual = false;
        current = slides.reduce((best, slide, index) => Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft) ? index : best, 0);
        update();
        if (wasManual) startTimer();
      }, 100);
    }, { signal, passive: true });
    const userInterrupted = event => {
      if (event.type === 'keydown' && !['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', ' '].includes(event.key)) return;
      if ((event.type === 'pointerdown' || event.type === 'touchstart') && event.target?.closest?.('button')) return;
      releasePendingCommand();
      current = slides.reduce((best, slide, index) => Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft) ? index : best, 0);
      update();
      startTimer();
    };
    ['wheel', 'pointerdown', 'touchstart', 'keydown'].forEach(type => track.addEventListener(type, userInterrupted, { signal, passive: true }));
    if (config.pauseOnHover === true) {
      track.addEventListener('mouseover', () => clearInterval(timer), { signal });
      track.addEventListener('mouseleave', startTimer, { signal });
    }
    if ('IntersectionObserver' in window) {
      visibleObserver = new IntersectionObserver(entries => entries.forEach(entry => entry.target.classList.toggle('np-brand-visible', entry.isIntersecting)), { root: track, threshold: 0.9 });
      slides.forEach(slide => visibleObserver.observe(slide));
    }
    window.addEventListener('resize', measure, { signal });
    states.set(owner, { destroy() { abort.abort(); clearInterval(timer); clearTimeout(scrollTimer); visibleObserver?.disconnect(); } });
    measure();
  }
  function init(root = document) {
    const owners = [...root.querySelectorAll('[data-native-carousel]')];
    if (root.matches?.('[data-native-carousel]')) owners.unshift(root);
    owners.forEach(initialize);
  }
  window.NativeBrandCarousels = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

/* Native lazy-background lifecycle; source evidence is background-contract.json. */
(function installNativeBrandBackground() {
  'use strict';
  if (window.NativeBrandBackground) return;
  const selector = '.nps-57fdb6011cd3';
  const marker = 'nps-57fdb6011cd3';
  function elements(root) {
    const list = Array.from(root.querySelectorAll(selector));
    if (typeof root.matches === 'function' && root.matches(selector)) list.unshift(root);
    return list;
  }
  function init(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    window.setTimeout(function initializeObserver() {
      if ('IntersectionObserver' in window) {
        const observer = new window.IntersectionObserver(function reveal(entries) {
          entries.forEach(function onEntry(entry) {
            if (entry.isIntersecting) {
              entry.target.classList.remove(marker);
              observer.unobserve(entry.target);
            }
          });
        }, {rootMargin: '5%'});
        elements(root).forEach(element => observer.observe(element));
      } else {
        window.setTimeout(function noObserverFallback() {
          elements(root).forEach(element => element.classList.remove(marker));
        }, 3000);
      }
    });
    window.setTimeout(function maximumDelayFallback() {
      elements(root).forEach(element => element.classList.remove(marker));
    }, 10000);
  }
  window.NativeBrandBackground = {init};
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  } else {
    init(document);
  }
})();
